import { useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { Button as AriaButton } from "react-aria-components";
import { cn } from "@/utils/helpers";
import type {
  AspectRatio,
  CalendarMediaType,
  CalendarOrientation,
  CalendarVideoLength,
} from "@/types/contentCalendar.type";
import {
  WEEKDAY_SHORT,
  cellOverflow,
  dayMonthShort,
  dayShort,
  dropRule,
  matchesFilters,
  postsByDay,
  sixWeekGrid,
  type CalendarFilters,
  type CalendarPost,
  type DayCell,
  type DropRule,
} from "./calendarModel";
import CalendarPostCard, { type PeekIntent } from "./CalendarPostCard";
import { CalendarTip } from "./CalendarParts";
import { CardSkeleton } from "./CalendarStates";

/** What a new post needs. The endpoint's required shape, and nothing more. */
export interface NewPost {
  day: number;
  title: string;
  mediaType: CalendarMediaType;
  aspectRatio: AspectRatio;
  orientation: CalendarOrientation;
  videoLength: CalendarVideoLength | null;
  pillarId: string | null;
  pillarName: string;
  /** A quick static post's slides: 2 or more make it a carousel. */
  slideCount?: number;
}

export interface MoveRequest {
  slotId: string;
  day: number;
  position: number;
}

/** A post lifted for a move, by the pointer or by the keyboard. */
export interface Lift {
  post: CalendarPost;
  mode: "pointer" | "keyboard";
  target: DayCell | null;
}

export interface GridCallbacks {
  /** A click or Enter on a card: open the post. */
  onOpenPost: (post: CalendarPost, anchor: HTMLElement) => void;
  /** Hover or keyboard focus on a card: its peek. */
  peekFor?: (post: CalendarPost) => PeekIntent;
  onOpenDay: (cell: DayCell, posts: CalendarPost[], anchor: HTMLElement) => void;
  onAddDay?: (cell: DayCell) => void;
  onMove?: (move: MoveRequest) => Promise<void> | void;
  onFocusDay?: (cell: DayCell) => void;
  onLiftChange?: (lift: Lift | null) => void;
}

/**
 * Where a move may land, shared by the month and the week. The grid owns the
 * lift; a day asks this whether it takes the post and why not.
 */
export function useLift({
  monthName,
  closed,
  clientName,
  onMove,
  onLiftChange,
}: {
  monthName: string;
  closed: boolean;
  clientName?: string | null;
  onMove?: (move: MoveRequest) => Promise<void> | void;
  onLiftChange?: (lift: Lift | null) => void;
}) {
  const [lift, setLiftState] = useState<Lift | null>(null);
  const setLift = (next: Lift | null) => {
    setLiftState(next);
    onLiftChange?.(next);
  };
  const ruleFor = (cell: DayCell): DropRule | null =>
    lift ? dropRule(lift.post.slot, cell, { monthName, closed, clientName }) : null;
  const drop = (cell: DayCell, position: number) => {
    if (!lift || !onMove) return setLift(null);
    const rule = dropRule(lift.post.slot, cell, { monthName, closed, clientName });
    const slotId = lift.post.slot.id;
    setLift(null);
    if (!rule.allowed) return;
    // Put back where it was: nothing to move.
    if (lift.post.slot.scheduledAt.slice(0, 10) === cell.key && position === lift.post.slot.order) return;
    void Promise.resolve(onMove({ slotId, day: cell.day, position })).catch(() => undefined);
  };
  return { lift, setLift, ruleFor, drop };
}

/**
 * Title lines that fit in a month day without covering the turn line.
 * A 150px row (the floor) holds two. Three need ~172, four ~190, counting
 * the date row, the 8px pad and gap, and a four-line card's chrome.
 */
function titleLinesFor(height: number, cap: 3 | 4): 2 | 3 | 4 {
  const fitted = height >= 190 ? 4 : height >= 172 ? 3 : 2;
  return Math.min(cap, fitted) as 2 | 3 | 4;
}

function useTitleLines(cellRef: React.RefObject<HTMLElement | null>, cap: 3 | 4) {
  const [lines, setLines] = useState<2 | 3 | 4>(2);
  useLayoutEffect(() => {
    const cell = cellRef.current;
    if (!cell) return;
    const measure = () => {
      const next = titleLinesFor(cell.clientHeight, cap);
      setLines((previous) => (previous === next ? previous : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(cell);
    return () => observer.disconnect();
  }, [cellRef, cap]);
  return lines;
}

/** How many cards a day shows, measured against the cell rather than guessed. */
function useFit(listRef: React.RefObject<HTMLDivElement | null>, count: number, titleLines: number) {
  const [fit, setFit] = useState({ visible: count, roomy: true });
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const box = list.clientHeight;
      const items = Array.from(list.children) as HTMLElement[];
      const bottoms = items.map((item) => item.offsetTop + item.offsetHeight);
      const fitAll = bottoms.filter((bottom) => bottom <= box + 1).length;
      const fitWithMore = bottoms.filter((bottom) => bottom <= box - 24 + 1).length;
      const next = { visible: cellOverflow(items.length, fitAll, fitWithMore).visible, roomy: fitWithMore > 0 };
      setFit((previous) => (previous.visible === next.visible && previous.roomy === next.roomy ? previous : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [listRef, count, titleLines]);
  // `roomy` is false when even one card leaves no room for the more line under it.
  return { visible: Math.min(fit.visible, count), roomy: fit.roomy };
}

/**
 * The month's height: whatever the view leaves under the header and above the
 * scope (Pen: five weeks of 192 at 1180), never less than `min`, so a short
 * window scrolls the page under the glass header instead of squeezing days.
 */
function useFillHeight(ref: React.RefObject<HTMLDivElement | null>, min: number) {
  const [height, setHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const grid = ref.current;
    if (!grid) return;
    const scroller = (grid.closest(".co-grid-field") as HTMLElement | null) ?? document.documentElement;
    const measure = () => {
      const top = grid.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
      const page = grid.closest<HTMLElement>("[data-cal-root]");
      const foot = page?.querySelector<HTMLElement>("[data-cal-scope]")?.offsetHeight ?? 0;
      // What sits under the page in the scroller: main's bottom padding, less
      // what the desktop page takes back with its negative margin.
      const main = page?.parentElement;
      const under = Math.max(
        0,
        (main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0) +
          (page ? parseFloat(getComputedStyle(page).marginBottom) || 0 : 0),
      );
      const next = Math.max(min, Math.floor(scroller.clientHeight - top - foot - under));
      setHeight((previous) => (previous === next ? previous : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    const root = grid.closest("[data-cal-root]");
    if (root) observer.observe(root);
    return () => observer.disconnect();
  }, [ref, min]);
  return height;
}

/** The + beside a day's date, with its tooltip and key. */
export function DayAdd({ cell, onAdd, className }: { cell: DayCell; onAdd: (cell: DayCell) => void; className?: string }) {
  const label = `Add a post on ${dayShort(cell)}`;
  return (
    <CalendarTip label={label} keys={["N"]} placement="bottom end" delay={300}>
      <AriaButton
        aria-label={label}
        onPress={() => onAdd(cell)}
        className={cn(
          // Day add: a 26px control-glass circle with a 15px plus.
          "co-glass-control grid size-[26px] shrink-0 place-items-center rounded-pill text-co-text outline-none transition-opacity",
          "focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-co-accent",
          className,
        )}
      >
        <Plus className="size-[15px]" aria-hidden="true" />
      </AriaButton>
    </CalendarTip>
  );
}

/** The date numeral, Barlow Condensed 600; today sits in a 26px accent circle (Calendar / Today marker). */
export function DayNumeral({ cell, isToday, quiet, size = 17 }: { cell: DayCell; isToday: boolean; quiet: boolean; size?: number }) {
  if (isToday) {
    return (
      <span
        className="grid size-[26px] shrink-0 place-items-center rounded-full bg-co-accent-fill font-section font-bold leading-none text-co-on-accent tabular-nums"
        style={{ fontSize: Math.min(size, 15) }}
      >
        {cell.day}
      </span>
    );
  }
  return (
    <span
      className={cn(
        "font-section font-semibold leading-5 tabular-nums",
        !cell.inMonth ? "text-co-faint" : quiet ? "text-co-muted" : "text-co-text",
      )}
      style={{ fontSize: size }}
    >
      {cell.day}
    </span>
  );
}

/** Keys while a post is lifted by the keyboard: arrows choose a day, Space drops, Escape puts it back. */
export function liftKeys(
  event: KeyboardEvent,
  lift: Lift | null,
  cells: DayCell[],
  columns: number,
  setLift: (lift: Lift | null) => void,
  drop: (cell: DayCell, position: number) => void,
  positionOf: (cell: DayCell) => number,
): boolean {
  if (!lift || lift.mode !== "keyboard") return false;
  const index = cells.findIndex((cell) => cell.key === lift.target?.key);
  const step =
    event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : event.key === "ArrowDown" ? columns : event.key === "ArrowUp" ? -columns : 0;
  if (step) {
    const next = cells[Math.max(0, Math.min(cells.length - 1, index + step))];
    setLift({ ...lift, target: next });
    return true;
  }
  if (event.key === " " || event.key === "Enter") {
    if (lift.target) drop(lift.target, positionOf(lift.target));
    else setLift(null);
    return true;
  }
  if (event.key === "Escape") {
    setLift(null);
    return true;
  }
  return false;
}

function MonthDay({
  cell,
  posts,
  isToday,
  past,
  filters,
  canAdd,
  canMove,
  lift,
  rule,
  over,
  onDragOverDay,
  onDropDay,
  onCardKeyDown,
  onLiftCard,
  onPointerLift,
  onPointerEnd,
  callbacks,
  cardRefs,
  skeleton,
  oneLine,
}: {
  oneLine: boolean;
  skeleton: boolean;
  cell: DayCell;
  posts: CalendarPost[];
  isToday: boolean;
  past: boolean;
  filters: CalendarFilters;
  canAdd: boolean;
  canMove: boolean;
  lift: Lift | null;
  rule: DropRule | null;
  over: boolean;
  onDragOverDay: (cell: DayCell, clientY: number, allowed: boolean) => void;
  onDropDay: (cell: DayCell, clientY: number) => void;
  onCardKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  onLiftCard: (post: CalendarPost, cell: DayCell) => void;
  onPointerLift: (post: CalendarPost) => void;
  onPointerEnd: () => void;
  callbacks: GridCallbacks;
  cardRefs: React.RefObject<Map<string, HTMLElement>>;
}) {
  const cellRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const titleLines = useTitleLines(cellRef, oneLine ? 3 : 4);
  const { visible, roomy } = useFit(listRef, posts.length, titleLines);
  const more = posts.length - visible;
  const lifting = !!lift;
  const tinted = lifting && !!rule?.allowed;
  let tip: ReactNode = null;
  if (lifting && over && rule) {
    tip = rule.allowed ? rule.consequence : rule.reason;
  }

  return (
    <div
      ref={cellRef}
      role="gridcell"
      aria-label={dayMonthShort(cell)}
      data-day-key={cell.key}
      onFocusCapture={() => callbacks.onFocusDay?.(cell)}
      onMouseEnter={() => callbacks.onFocusDay?.(cell)}
      onDragOver={(event) => {
        if (!lifting) return;
        if (rule?.allowed) {
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
        }
        onDragOverDay(cell, event.clientY, !!rule?.allowed);
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDropDay(cell, event.clientY);
      }}
      className={cn(
        // Calendar / Month day: pad 8, gap 8; a hairline on the right except the week's last day.
        // Hairlines as inset shadows: Pen strokes take no room from the 8px pad.
        "group/day relative flex min-h-0 min-w-0 flex-col gap-2 overflow-hidden p-2",
        "shadow-[inset_0_1px_0_var(--co-line)] [&:not(:nth-child(7n))]:shadow-[inset_0_1px_0_var(--co-line),inset_-1px_0_0_var(--co-line)]",
        "transition-colors duration-[var(--duration-short)] ease-[var(--ease-out-expo)]",
        cell.inMonth && !lifting && "hover:bg-co-hover focus-within:bg-co-hover",
        tinted && "bg-co-accent-dim",
        over && lifting && (rule?.allowed ? "outline outline-2 -outline-offset-2 outline-co-accent" : "outline outline-1 -outline-offset-1 outline-co-line2"),
      )}
    >
      <div className="flex min-h-5 shrink-0 items-center gap-1.5">
        <DayNumeral cell={cell} isToday={isToday} quiet={past} />
        {more > 0 && !roomy ? (
          <button
            type="button"
            onClick={(event) => callbacks.onOpenDay(cell, posts, event.currentTarget)}
            className="h-5 text-[12px] text-co-muted outline-none hover:text-co-text hover:underline focus-visible:outline-2 focus-visible:outline-co-accent"
          >
            +{more} more
          </button>
        ) : null}
        {canAdd && !lifting ? (
          <DayAdd
            cell={cell}
            onAdd={(c) => callbacks.onAddDay?.(c)}
            className="absolute right-1.5 top-1.5 opacity-0 group-hover/day:opacity-100 group-focus-within/day:opacity-100"
          />
        ) : null}
      </div>
      <div ref={listRef} className="relative flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
        {skeleton ? <CardSkeleton /> : null}
        {posts.map((post, index) => {
          const hidden = index >= visible;
          const isLifted = lift?.post.slot.id === post.slot.id;
          return (
            <div
              key={post.slot.id}
              ref={(node) => {
                if (node) cardRefs.current.set(post.slot.id, node);
                else cardRefs.current.delete(post.slot.id);
              }}
              className={cn(hidden && "invisible")}
              inert={hidden || undefined}
            >
              <CalendarPostCard
                post={post}
                titleLines={titleLines}
                dimmed={!matchesFilters(post, filters)}
                lifted={isLifted}
                draggable={canMove && post.slot.status !== "IN_CLIENT_REVIEW"}
                hint={
                  canMove
                    ? post.slot.status === "IN_CLIENT_REVIEW"
                      ? "It can't move while the client decides."
                      : "Space lifts it to move."
                    : undefined
                }
                onOpen={(anchor) => callbacks.onOpenPost(post, anchor)}
                peek={callbacks.peekFor?.(post)}
                onLift={canMove ? () => onLiftCard(post, cell) : undefined}
                onKeyDown={onCardKeyDown}
                onDragStart={(event) => {
                  event.dataTransfer.setData("text/plain", post.slot.id);
                  event.dataTransfer.effectAllowed = "move";
                  onPointerLift(post);
                }}
                onDragEnd={onPointerEnd}
              />
            </div>
          );
        })}
        {lift && lift.mode === "keyboard" && over && rule?.allowed && lift.target?.key === cell.key &&
        !posts.some((post) => post.slot.id === lift.post.slot.id) ? (
          <div aria-hidden="true">
            <CalendarPostCard post={lift.post} titleLines={titleLines} active onOpen={() => undefined} />
          </div>
        ) : null}
      </div>
      {more > 0 && roomy ? (
        <button
          type="button"
          onClick={(event) => callbacks.onOpenDay(cell, posts, event.currentTarget)}
          className="absolute bottom-1.5 left-2 h-6 text-[12px] text-co-muted outline-none hover:text-co-text hover:underline focus-visible:outline-2 focus-visible:outline-co-accent"
        >
          +{more} more
        </button>
      ) : null}
      {tip ? (
        <span
          role="status"
          className="co-glass-chrome pointer-events-none absolute inset-x-2 bottom-2 z-10 rounded-tip px-2 py-1 text-[12px] leading-snug text-co-text"
        >
          {tip}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The month as six weeks of seven days (DESIGN.md "The Calendar in Parts").
 * Each row is at least one whole card plus the date row (180px), so a short
 * viewport scrolls the grid under the sticky header rather than squeezing it.
 * Cells are equal height and fill the view down to the scope bar; a day with
 * more posts than fit says "+{n} more" and opens its list in the peek.
 */
export default function MonthGrid({
  year,
  month,
  monthName,
  posts,
  filters,
  todayUtc,
  closed,
  canAdd,
  clientName,
  callbacks,
  skeleton = false,
  oneLine = false,
}: {
  /** A tablet's month: one-line card titles. */
  oneLine?: boolean;
  year: number;
  /** 1-12. */
  month: number;
  monthName: string;
  /** The month is loading: placeholder cards, header live. */
  skeleton?: boolean;
  posts: CalendarPost[];
  filters: CalendarFilters;
  todayUtc: number;
  /** A closed or past month: read only. */
  closed: boolean;
  canAdd: boolean;
  clientName?: string | null;
  callbacks: GridCallbacks;
}) {
  // Only the weeks the month touches (five for most months), like the Pen month.
  const cells = useMemo(() => {
    const all = sixWeekGrid(year, month);
    const lastDay = all.map((cell) => cell.inMonth).lastIndexOf(true);
    const weeks = Math.ceil((lastDay + 1) / 7);
    return all.slice(0, weeks * 7);
  }, [year, month]);
  const weeks = cells.length / 7;
  const gridRef = useRef<HTMLDivElement>(null);
  const fill = useFillHeight(gridRef, 35 + weeks * 150);
  const byDay = useMemo(() => postsByDay(posts), [posts]);
  const cardRefs = useRef(new Map<string, HTMLElement>());
  const canMove = !closed && !!callbacks.onMove;
  const { lift, setLift, ruleFor, drop } = useLift({
    monthName,
    closed,
    clientName,
    onMove: callbacks.onMove,
    onLiftChange: callbacks.onLiftChange,
  });
  const [pointerOver, setPointerOver] = useState<string | null>(null);

  /* Which index in a day the pointer is aiming at: each card's midpoint. */
  const positionIn = (cell: DayCell, clientY: number | null) => {
    const list = (byDay.get(cell.key) ?? []).filter((post) => post.slot.id !== lift?.post.slot.id);
    if (clientY === null) return list.length;
    let index = 0;
    for (const post of list) {
      const box = cardRefs.current.get(post.slot.id)?.getBoundingClientRect();
      if (box && clientY > box.top + box.height / 2) index += 1;
    }
    return index;
  };

  const overKey = lift?.mode === "keyboard" ? (lift.target?.key ?? null) : pointerOver;

  return (
    <div
      role="grid"
      aria-label={`${monthName} ${year}`}
      aria-roledescription="calendar"
      ref={gridRef}
      className="grid shrink-0 grid-cols-7"
      style={{ gridTemplateRows: `auto repeat(${weeks}, minmax(150px, 1fr))`, height: fill ?? undefined }}
      data-calendar-grid=""
    >
      <div role="row" className="contents">
        {WEEKDAY_SHORT.map((name) => (
          <div
            key={name}
            role="columnheader"
            className="px-2.5 pb-2 pt-2.5 text-[12.5px] leading-[17px] text-co-muted"
          >
            {name}
          </div>
        ))}
      </div>
      {Array.from({ length: weeks }, (_, week) => (
        <div role="row" key={week} className="contents">
          {cells.slice(week * 7, week * 7 + 7).map((cell) => {
            const dayPosts = cell.inMonth ? (byDay.get(cell.key) ?? []) : [];
            const past = cell.inMonth && cell.utc < todayUtc;
            return (
              <MonthDay
                key={cell.key}
                cell={cell}
                posts={dayPosts}
                isToday={cell.utc === todayUtc}
                past={past || closed}
                filters={filters}
                canAdd={canAdd && !closed && cell.inMonth && cell.utc >= todayUtc}
                canMove={canMove}
                lift={lift}
                rule={ruleFor(cell)}
                over={overKey === cell.key}
                onDragOverDay={(c) => setPointerOver(c.key)}
                onDropDay={(c, clientY) => {
                  setPointerOver(null);
                  drop(c, positionIn(c, clientY));
                }}
                onCardKeyDown={(event) => {
                  const handled = liftKeys(event, lift, cells, 7, setLift, drop, (c) => positionIn(c, null));
                  if (handled) {
                    event.preventDefault();
                    event.stopPropagation();
                  }
                }}
                onLiftCard={(post, c) => setLift({ post, mode: "keyboard", target: c })}
                onPointerLift={(post) => setLift({ post, mode: "pointer", target: null })}
                onPointerEnd={() => {
                  setPointerOver(null);
                  if (lift?.mode === "pointer") setLift(null);
                }}
                callbacks={callbacks}
                cardRefs={cardRefs}
                skeleton={skeleton && cell.inMonth && (cell.day * 5) % 11 === 3}
                oneLine={oneLine}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}
